import React, { useMemo, useRef } from "react";
import { ViewData } from "../types";
import { hexUint8Array } from "../utils";
import "./Timeline.css";

interface TimelineProps {
  views: ViewData[];
}

interface StatusBadgeProps {
  color: "blue" | "black" | "green";
  label: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ color, label }) => (
  <div className="timeline__legend-badge">
    <div className={`timeline__legend-dot timeline__legend-dot--${color}`} />
    <span className="timeline__legend-label">{label}</span>
  </div>
);

const Timeline: React.FC<TimelineProps> = ({ views }) => {
  const currentTimeRef = useRef(Date.now());

  // Update current time reference
  currentTimeRef.current = Date.now();

  // Get the most recent 10 views for display
  const displayViews = useMemo(() => {
    return views.slice(0, 10);
  }, [views]);

  // Calculate max duration for scaling
  const maxDuration = useMemo(() => {
    let max = 1000; // minimum 1 second scale
    displayViews.forEach((view) => {
      if (view.finalizationTime && view.startTime) {
        max = Math.max(max, view.finalizationTime - view.startTime);
      } else if (view.notarizationTime && view.startTime) {
        max = Math.max(max, view.notarizationTime - view.startTime);
      } else if (view.startTime) {
        max = Math.max(max, currentTimeRef.current - view.startTime);
      }
    });
    return max * 1.2; // Add 20% padding
  }, [displayViews]);

  const getBarWidth = (startTime: number, endTime: number): number => {
    const duration = endTime - startTime;
    return Math.min((duration / maxDuration) * 100, 100);
  };

  const formatHash = (hash?: Uint8Array): string => {
    if (!hash) return "";
    return hexUint8Array(hash, 10);
  };

  return (
    <section className="timeline">
      {/* Header */}
      <div className="timeline__header">
        <h2 className="timeline__title">Timeline</h2>
        <div className="timeline__legend">
          <StatusBadge color="blue" label="Seeded" />
          <StatusBadge color="black" label="Locked" />
          <StatusBadge color="green" label="Finalized" />
        </div>
      </div>

      {/* Timeline Items */}
      <div className="timeline__items">
        {displayViews.map((view) => (
          <TimelineItem
            key={view.view}
            view={view}
            maxDuration={maxDuration}
            currentTime={currentTimeRef.current}
            getBarWidth={getBarWidth}
            formatHash={formatHash}
          />
        ))}
      </div>
    </section>
  );
};

interface TimelineItemProps {
  view: ViewData;
  maxDuration: number;
  currentTime: number;
  getBarWidth: (startTime: number, endTime: number) => number;
  formatHash: (hash?: Uint8Array) => string;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
  view,
  currentTime,
  getBarWidth,
  formatHash,
}) => {
  const { startTime, notarizationTime, finalizationTime, block, status } = view;

  // Calculate bar widths
  let seededWidth = 0;
  let lockedWidth = 0;
  let finalizedWidth = 0;

  if (startTime) {
    if (status === "growing" || status === "unknown") {
      // Still growing - show seeded bar expanding
      seededWidth = getBarWidth(startTime, currentTime);
    } else if (notarizationTime) {
      // Has been locked
      seededWidth = getBarWidth(startTime, notarizationTime);

      if (finalizationTime) {
        // Has been finalized
        lockedWidth = getBarWidth(notarizationTime, finalizationTime);
        finalizedWidth = 5; // Small cap to show finalized state
      } else {
        // Still waiting for finalization
        lockedWidth = getBarWidth(notarizationTime, currentTime);
      }
    } else if (finalizationTime) {
      // Finalized but no notarization recorded (rare case)
      seededWidth = getBarWidth(startTime, finalizationTime);
      finalizedWidth = 5;
    }
  }

  const viewNumber = view.view.toString();
  const blockHash = block?.digest ? formatHash(block.digest) : "";
  const parentHash = block?.parent ? formatHash(block.parent) : "";
  const blockHeight = block?.height;

  return (
    <div className="timeline__item">
      {/* View Info */}
      <div className="timeline__view-info">
        <span className="timeline__view-number">{viewNumber}</span>
        {blockHash && (
          <a href="#" className="timeline__view-hash">
            {blockHash}
          </a>
        )}
      </div>

      {/* Progress Bars */}
      <div className="timeline__bars">
        <div className="timeline__bar-track">
          {/* Seeded bar (blue) */}
          {seededWidth > 0 && (
            <div
              className="timeline__bar timeline__bar--seeded"
              style={{ width: `${seededWidth}%` }}
            >
              {status === "growing" || status === "unknown" ? null : (
                <span className="timeline__bar-label">#{viewNumber}</span>
              )}
            </div>
          )}
          {/* Locked bar (black/dark) */}
          {lockedWidth > 0 && (
            <div
              className="timeline__bar timeline__bar--locked"
              style={{ width: `${lockedWidth}%` }}
            >
              {parentHash && (
                <span className="timeline__bar-label">{parentHash}</span>
              )}
            </div>
          )}
          {/* Finalized bar (green) */}
          {finalizedWidth > 0 && (
            <div
              className="timeline__bar timeline__bar--finalized"
              style={{ width: `${finalizedWidth}%` }}
            />
          )}
        </div>

        {/* Block height marker */}
        {blockHeight !== undefined && (
          <div className="timeline__markers">
            <span className="timeline__marker">{blockHeight}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Timeline;
